import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GenerateCvService } from 'src/app/pages/generate resume/generate-cv-steps/services/generate-cv/generate-cv.service';

@Component({
  selector: 'app-select-resume',
  templateUrl: './select-resume.component.html',
  styleUrls: ['./select-resume.component.css'],
})
export class SelectResumeComponent {
  @Input() formGroup!: FormGroup;

  selectedResumeType: string = 'scratch';
  @Output() next = new EventEmitter<any>();
  @Output() previous = new EventEmitter<void>();
  IsUploadCv = false;
  loading = false;
  cv: any = null;

  constructor(
    private generateCvService: GenerateCvService,
    private fb: FormBuilder
  ) {}

  onCVDrop(event: any) {
    event.preventDefault();
    this.cv = event.dataTransfer.files[0];
    this.uploadCV(this.cv);
  }

  onCVSelected(event: any) {
    this.cv = event.target.files[0];
    this.uploadCV(this.cv);
  }

  private uploadCV(file: File) {
    if (!file) return;

    const formData = new FormData();
    formData.append('cv', file);
    this.loading = true;

    this.generateCvService.uploadExistingCv(formData).subscribe({
      next: (cvJson: any) => {
        this.patchFormValues(cvJson);

        // انتظر حتى يكمل Angular الـ change detection
        setTimeout(() => {
          // احفظ الـ form data
          this.saveFormDataToLocalStorage();

          this.loading = false;

          // انتظر شوية عشان الـ UI يتحدث
          setTimeout(() => {
            this.next.emit();
          }, 100);
        }, 200);
      },
      error: (error) => {
        console.error('Error uploading CV:', error);
        this.loading = false;
        alert('حدث خطأ أثناء تحميل السيرة الذاتية. يرجى المحاولة مرة أخرى.');
      },
    });
  }

  private saveFormDataToLocalStorage() {
    const formValue = { ...this.formGroup.value };

    // تأكد من أن skill و course arrays of strings
    if (formValue.skill && Array.isArray(formValue.skill)) {
      formValue.skill = formValue.skill
        .map((item: any) =>
          typeof item === 'string' ? item : item?.name || ''
        )
        .filter((item: string) => item.trim() !== '');
    }

    if (formValue.course && Array.isArray(formValue.course)) {
      formValue.course = formValue.course
        .map((item: any) =>
          typeof item === 'string' ? item : item?.name || ''
        )
        .filter((item: string) => item.trim() !== '');
    }

    const formData = {
      formValue: formValue,
      activeStep: 3, // Personal Data step
    };

    localStorage.setItem('cvFormData', JSON.stringify(formData));
    localStorage.setItem('activeStep', '3');
  }

  patchFormValues(cvJson: any) {
    // Patch simple fields
    this.formGroup.patchValue({
      name: cvJson?.name || '',
      title: cvJson?.title || '',
      city: cvJson?.city || '',
      phone: cvJson?.phone || '',
      linkedIn: cvJson?.linkedIn || '',
      country: cvJson?.country || '',
      email: cvJson?.email || '',
      summary: cvJson?.summary || '',
      portfolio: cvJson?.portfolio || '',
    });

    // Update form arrays
    if (cvJson?.skill && Array.isArray(cvJson.skill)) {
      this.updateFormArray('skill', cvJson.skill);
    }

    if (cvJson?.languages && Array.isArray(cvJson.languages)) {
      this.updateLanguages(cvJson.languages);
    }

    if (cvJson?.course && Array.isArray(cvJson.course)) {
      this.updateFormArray('course', cvJson.course);
    }

    if (cvJson?.certification && Array.isArray(cvJson.certification)) {
      this.updateCertifications(cvJson.certification);
    }

    if (cvJson?.project && Array.isArray(cvJson.project)) {
      this.updateProjects(cvJson.project);
    }

    if (cvJson?.experiences && Array.isArray(cvJson.experiences)) {
      this.updateExperiences(cvJson.experiences);
    }

    if (cvJson?.education && Array.isArray(cvJson.education)) {
      this.updateEducation(cvJson.education);
    }

    // Mark form as dirty to trigger validation
    this.formGroup.markAsDirty();
    this.formGroup.updateValueAndValidity();
  }

  updateFormArray(field: string, values: any[]) {
    const control = this.formGroup.get(field) as FormArray;
    if (!control) return;

    control.clear();

    values.forEach((value) => {
      const stringValue = typeof value === 'string' ? value : value?.name || '';
      if (stringValue.trim() !== '') {
        control.push(this.fb.control(stringValue, Validators.required));
      }
    });

    control.updateValueAndValidity();
  }

  updateLanguages(languages: any[]) {
    const control = this.formGroup.get('languages') as FormArray;
    if (!control) return;

    control.clear();

    languages.forEach((lang) => {
      if (lang && (lang.name || lang.level)) {
        control.push(
          this.fb.group({
            name: [lang.name || '', Validators.required],
            level: [lang.level || ''],
          })
        );
      }
    });

    control.updateValueAndValidity();
  }

  updateCertifications(certifications: any[]) {
    const control = this.formGroup.get('certification') as FormArray;
    if (!control) return;

    control.clear();

    certifications.forEach((cert) => {
      if (cert && (cert.name || cert.organization)) {
        control.push(
          this.fb.group({
            name: [cert.name || '', Validators.required],
            organization: [cert.organization || '', Validators.required],
            date: [cert.date || '', Validators.required],
          })
        );
      }
    });

    control.updateValueAndValidity();
  }

  updateProjects(projects: any[]) {
    const control = this.formGroup.get('project') as FormArray;
    if (!control) return;

    control.clear();

    projects.forEach((proj) => {
      if (proj && proj.name) {
        control.push(
          this.fb.group({
            name: [proj.name || '', Validators.required],
            link: [proj.link || ''],
            role: [proj.role || ''],
          })
        );
      }
    });

    control.updateValueAndValidity();
  }

  updateExperiences(experiences: any[]) {
    const control = this.formGroup.get('experiences') as FormArray;
    if (!control) return;

    control.clear();

    experiences.forEach((exp) => {
      if (exp && (exp.title || exp.company)) {
        const expGroup = this.fb.group({
          title: [exp.title || '', Validators.required],
          startDate: [this.formatDate(exp.startDate), Validators.required],
          endDate: [this.formatDate(exp.endDate)],
          company: [exp.company || '', Validators.required],
          isCurrent: [exp.isCurrent || false],
          dutiesAchievements: [exp.dutiesAchievements || ''],
        });

        // Setup isCurrent watcher
        expGroup.get('isCurrent')?.valueChanges.subscribe((isCurrent) => {
          const endDateControl = expGroup.get('endDate');
          if (isCurrent) {
            endDateControl?.clearValidators();
            endDateControl?.setValue('');
            endDateControl?.disable();
          } else {
            endDateControl?.enable();
            endDateControl?.setValidators([Validators.required]);
          }
          endDateControl?.updateValueAndValidity();
        });

        control.push(expGroup);
      }
    });

    control.updateValueAndValidity();
  }

  updateEducation(education: any[]) {
    const control = this.formGroup.get('education') as FormArray;
    if (!control) return;

    control.clear();

    education.forEach((edu) => {
      if (edu && (edu.major || edu.university)) {
        const endDate = this.formatDate(edu.endDate);

        const eduGroup = this.fb.group({
          major: [
            edu.major || '',
            [Validators.required, Validators.minLength(2)],
          ],
          university: [
            edu.university || '',
            [Validators.required, Validators.minLength(2)],
          ],
          degree: [edu.degree || ''],
          endDate: [endDate, Validators.required],
          grade: [edu.grade || ''],
          details: [edu.details || ''],
          isCurrent: [edu.isCurrent || false],
        });

        // Setup isCurrent watcher
        eduGroup.get('isCurrent')?.valueChanges.subscribe((isCurrent) => {
          const endDateControl = eduGroup.get('endDate');
          if (isCurrent) {
            endDateControl?.clearValidators();
            endDateControl?.setValue('');
          } else {
            endDateControl?.setValidators([Validators.required]);
          }
          endDateControl?.updateValueAndValidity();
        });

        control.push(eduGroup);
      }
    });

    control.updateValueAndValidity();
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';

    // Handle year-only format (e.g., "2024")
    if (/^\d{4}$/.test(dateString.trim())) {
      return `${dateString}-01-01`;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onNext() {
    if (this.selectedResumeType === 'upload') {
      this.IsUploadCv = true;
    } else {
      this.next.emit();
    }
  }

  onPrevious() {
    if (this.IsUploadCv === true) {
      this.IsUploadCv = false;
    } else {
      this.previous.emit();
    }
  }
}
